import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import {
  getBanners,
  saveBanners,
  getPromotional,
  savePromotional,
} from "../db";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Header from "./Header";
import { Container, Row, Col } from "react-bootstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import "react-medium-image-zoom/dist/styles.css";
import "swiper/css";
import "swiper/css/free-mode";
import "./../App.css";
import "swiper/css/pagination";
import "bootstrap/dist/css/bootstrap.min.css";
import Footer from "./Footer";
import debounce from "lodash/debounce";

export default function Home() {
  sessionStorage.setItem("last_visited_product", "");

  const dealer_id = localStorage.getItem("dealer_id");
  const app_cache_clear = localStorage.getItem("app_cache_clear");
  const token = localStorage.getItem("token");
  const server_url = localStorage.getItem("server_url");
  const LoginNumber = localStorage.getItem("LoginNumber");
  const image = localStorage.getItem("admin_message");
  const navigate = useNavigate();
  const [data, setData] = useState([]); // Banners
  const [orderShow, setOrderShow] = useState([]);
  const [data2, setData2] = useState([]); // Promotional
  const [customerExperience, setCustomerExperience] = useState([]); // Comments
  const [offer, setOffer] = useState([]); // Comments
  const [loading, setLoading] = useState(false); // Removed full sync loader
  const [isVisible, setIsVisible] = useState(
    image && !sessionStorage.getItem("imageSeen"),
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [quantitiesInitialized, setQuantitiesInitialized] = useState(false);
  // Add to cart states
  const [addingProducts, setAddingProducts] = useState(new Map());
  const [quantityInputs, setQuantityInputs] = useState({});
  const [showSteppers, setShowSteppers] = useState(new Map());

  const location = useLocation();
  const { pathname } = location;
  const [totalDealer, setTotalDealer] = useState(0);

  // Mobile detection with Safari check
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ------------------------------------------------------------------ *
   *  Initialize quantities from existing cart data
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (offer.length > 0 && !quantitiesInitialized) {
      const initialQuantities = {};
      const initialSteppers = new Map();

      offer.forEach((off) => {
        off.products.forEach((product) => {
          const productId = product.product_id;
          const existingQty = product.product_quantity || 0;

          initialQuantities[productId] = existingQty;
          initialSteppers.set(productId, existingQty > 0);
        });
      });

      setQuantityInputs(initialQuantities);
      setShowSteppers(initialSteppers);
      setQuantitiesInitialized(true);
    }
  }, [offer, quantitiesInitialized]);

  // Check if Safari browser
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      // Always load from cache first for immediate display
      const cachedBanners = await getBanners();
      const cachedPromo = await getPromotional();
      if (cachedBanners) setData(cachedBanners);
      if (cachedPromo) setData2(cachedPromo);

      // Then sync in background
      const mustSync = app_cache_clear !== "2";

      if (mustSync) {
        try {
          // Run all API calls in parallel without blocking UI
          Promise.all([
            ...(LoginNumber && LoginNumber.trim() ? [check_user()] : []),
            get_banner(),
            getPromotion(),
            getCustomerExperience(),
            getOffer(),
          ])
            .then(() => {
              sessionStorage.setItem("homeLoaded", "true");
            })
            .catch((err) => {
              console.log("Background sync failed:", err);
            });
        } catch (err) {
          // Silent fail - we already have cached data
          console.log("Sync error:", err);
        }
      } else {
        const menu = document.getElementById("menu-settings");
        if (menu) menu.classList.add("menu-active");
      }
    };

    initializeData();

    const handleBeforeUnload = () => sessionStorage.removeItem("homeLoaded");
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------------------------------------------ *
   *  Add to Cart Functionality
   * ------------------------------------------------------------------ */
  const addToCart = useCallback(
    async (productId, qty) => {
      if (!LoginNumber || LoginNumber.trim() === "" || !dealer_id) {
        const currentPath = location.pathname + location.search;
        localStorage.setItem("login_url", currentPath);
        navigate("/Logout");
        return;
      }

      if (!productId || qty < 0 || isNaN(qty)) return false;

      setAddingProducts((prev) => new Map(prev).set(productId, true));
      try {
        const formData = new FormData();
        formData.append("company_id", "2");
        formData.append("dealer_id", dealer_id);
        formData.append("product_id", productId);
        formData.append("qty", qty);

        const res = await axios.post(`${server_url}add_cart.php`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data.status === "1") {
          // Update local state
          setQuantityInputs((prev) => ({ ...prev, [productId]: qty }));
          setShowSteppers((prev) => new Map(prev).set(productId, qty > 0));

          // Update cart count in localStorage if needed
          const currentCount = parseInt(
            localStorage.getItem("local_items_in_cart") || "0",
          );
          if (qty > 0 && currentCount === 0) {
            localStorage.setItem("local_items_in_cart", "1");
          }

          return true;
        } else {
          toast.warning(res.data.error);
          return false;
        }
      } catch (e) {
        toast("Try again later.");
        return false;
      } finally {
        setAddingProducts((prev) => {
          const m = new Map(prev);
          m.delete(productId);
          return m;
        });
      }
    },
    [dealer_id, server_url, navigate],
  );

  const debouncedAddToCart = useCallback(debounce(addToCart, 500), [addToCart]);

  const handleQuantityChange = (productId, value) => {
    const qty = value === "" ? "" : parseInt(value, 10);
    setQuantityInputs((prev) => ({ ...prev, [productId]: value }));
    setShowSteppers((prev) => new Map(prev).set(productId, true));
    if (value === "" || (!isNaN(qty) && qty >= 0 && qty <= 99)) {
      if (qty === 0) {
        setShowSteppers((prev) => new Map(prev).set(productId, false));
        debouncedAddToCart(productId, 0);
      } else if (!isNaN(qty)) {
        debouncedAddToCart(productId, qty);
      }
    } else {
      toast("Quantity 0–99 only");
    }
  };

  const handleButtonHold = useCallback(
    (productId, action) => {
      let interval;
      const timeout = setTimeout(() => {
        interval = setInterval(() => {
          setQuantityInputs((prev) => {
            const current = parseInt(prev[productId] || 0, 10);
            const newQty =
              action === "increment"
                ? Math.min(current + 1, 99)
                : Math.max(current - 1, 0);
            setShowSteppers((prevMap) =>
              new Map(prevMap).set(productId, newQty > 0),
            );
            debouncedAddToCart(productId, newQty);
            return { ...prev, [productId]: newQty };
          });
        }, 200);
      }, 500);

      const stop = () => {
        clearTimeout(timeout);
        clearInterval(interval);
        document.removeEventListener("mouseup", stop);
        document.removeEventListener("touchend", stop);
      };
      document.addEventListener("mouseup", stop);
      document.addEventListener("touchend", stop);
    },
    [debouncedAddToCart],
  );

  const handleButtonClick = useCallback(
    (productId, action) => {
      setQuantityInputs((prev) => {
        const current = parseInt(prev[productId] || 0, 10);
        const newQty =
          action === "increment"
            ? Math.min(current + 1, 99)
            : Math.max(current - 1, 0);
        setShowSteppers((prevMap) =>
          new Map(prevMap).set(productId, newQty > 0),
        );
        debouncedAddToCart(productId, newQty);
        return { ...prev, [productId]: newQty };
      });
    },
    [debouncedAddToCart],
  );

  const handleAddClick = (productId) => {
    setQuantityInputs((prev) => ({ ...prev, [productId]: 1 }));
    setShowSteppers((prev) => new Map(prev).set(productId, true));
    debouncedAddToCart(productId, 1);
  };

  const handleProductClick = useCallback((productId) => {
    sessionStorage.setItem("last_visited_product", productId);
  }, []);

  /* ------------------------------------------------------------------ *
   *  API helpers
   * ------------------------------------------------------------------ */
  const check_user = async () => {
    if (!LoginNumber || LoginNumber.trim() === "") {
      return;
    }
    const formData = new FormData();
    formData.append("company_id", "2");
    formData.append("dealer_id", dealer_id);
    formData.append("device_token", token);

    const result = await axios.post(`${server_url}check_dealer.php`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (result.data.status === "1") {
      const r = result.data.result[0];
      const total_dealer = r.total_dealer || 0;
      setTotalDealer(total_dealer);

      localStorage.setItem("LoginNumber", r.dealer_login_mobile);
      localStorage.setItem("notificaion_count", r.dealer_notification_count);
      localStorage.setItem("local_overdue_amount", r.overdue_amount);
      localStorage.setItem("local_dealer_os", r.dealer_os);
      localStorage.setItem("order_date", r.order_date);
      localStorage.setItem("local_items_in_cart", r.items_in_cart);
      localStorage.setItem(
        "local_current_order_status",
        r.current_order_status,
      );
      localStorage.setItem(
        "local_order_final_total_with_gst",
        r.order_final_total_with_gst,
      );
      localStorage.setItem("local_order_id", r.order_id);
      localStorage.setItem("admin_message", r.admin_message);
      localStorage.setItem("local_dealer_firm_name", r.dealer_firm_name);
      localStorage.setItem("dealer_id", r.dealer_id);
      localStorage.setItem("dealer_firm_name", r.dealer_firm_name);
      localStorage.setItem(
        "dealer_order_varification_mobile",
        r.dealer_order_varification_mobile,
      );
      localStorage.setItem("dealer_login_mobile", r.dealer_login_mobile);
      localStorage.setItem("dealer_email", r.dealer_email);
      localStorage.setItem("dealer_address", r.dealer_address);
      localStorage.setItem("dealer_pincode", r.dealer_pincode);
      localStorage.setItem("dealer_pan_number", r.dealer_pan_number);
      localStorage.setItem("dealer_gst_number", r.dealer_gst_number);
      localStorage.setItem("city_name", r.city_name);
      localStorage.setItem("app_cache_clear", r.app_cache_clear);
      localStorage.setItem("order_count", r.order_count);
      localStorage.setItem("dealer_price_set", r.dealer_price_set);
      setOrderShow(r.order_count);
    } else {
      const currentPath = location.pathname + location.search;
      localStorage.setItem("login_url", currentPath);
      navigate("/Logout");
    }
  };

  const get_banner = async () => {
    const formData = new FormData();
    formData.append("company_id", "2");
    const res = await axios.post(`${server_url}banners.php`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (res.data.status === "1") {
      setData(res.data.result);
      await saveBanners(res.data.result);
    } else {
      toast.warning(res.data.error || "Failed to fetch banners");
    }
  };

  const getPromotion = async () => {
    const formData = new FormData();
    formData.append("company_id", "2");
    formData.append("promotion_type", "1");
    const res = await axios.post(`${server_url}promotion.php`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (res.data.status === "1") {
      setData2(res.data.result);
      await savePromotional(res.data.result);
    } else {
      toast.warning(res.data.error || "Failed to fetch promotions");
    }
  };

  const getCustomerExperience = async () => {
    const formData = new FormData();
    formData.append("company_id", "2");
    formData.append("dealer_id", dealer_id || "");
    formData.append("promotion_id", "");
    formData.append("product_id", "");
    formData.append("promotion_comment_by", "");
    formData.append("promotion_comment_id", "");
    const res = await axios.post(
      `${server_url}promotion_comment_list.php`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    if (res.data.status === "1") {
      setCustomerExperience(res.data.result);
    } else {
      // toast.warning(res.data.error || "Failed to fetch customer experiences");
    }
  };

  const getOffer = async () => {
    const formData = new FormData();
    formData.append("company_id", "2");
    formData.append("dealer_id", dealer_id || "");
    const res = await axios.post(`${server_url}offer.php`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (res.data.status === "1") {
      setOffer(res.data.result);
    } else {
      setOffer([]);
      // toast.warning(res.data.error || "Failed to fetch customer experiences");
    }
  };

  /* ------------------------------------------------------------------ *
   *  Add to Cart Component for Product Cards
   * ------------------------------------------------------------------ */
  const AddToCartButton = ({ product }) => {
    const productId = product.product_id;
    const accentColor = "rgb(219, 68, 55)"; // Using your accent color

    // Get initial quantity from product data, fallback to state
    const initialQty = product.product_quantity || 0;
    const currentQty = quantityInputs[productId] ?? initialQty;

    const isAdding = addingProducts.get(productId);
    const showStepper = showSteppers.get(productId) ?? currentQty > 0;

    return (
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          right: "10px",
          zIndex: 10,
        }}
      >
        {showStepper ? (
          <div
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              width: "100px",
              height: "30px",
              border: `1px solid ${accentColor}`,
            }}
          >
            <button
              style={{
                padding: "8px",
                color: accentColor,
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              onClick={() => handleButtonClick(productId, "decrement")}
              onMouseDown={() => handleButtonHold(productId, "decrement")}
              onTouchStart={() => handleButtonHold(productId, "decrement")}
              disabled={isAdding}
            >
              <i className="redcolor fa fa-minus"></i>
            </button>
            <input
              type="number"
              min="0"
              max="99"
              value={currentQty}
              onChange={(e) => handleQuantityChange(productId, e.target.value)}
              style={{
                width: "40px",
                textAlign: "center",
                background: "transparent",
                border: "none",
                color: "#000",
              }}
              disabled={isAdding}
            />
            <button
              style={{
                padding: "8px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              onClick={() => handleButtonClick(productId, "increment")}
              onMouseDown={() => handleButtonHold(productId, "increment")}
              onTouchStart={() => handleButtonHold(productId, "increment")}
              disabled={isAdding}
            >
              <i
                className="redcolor fa fa-plus"
                style={{ color: accentColor }}
              ></i>
            </button>
          </div>
        ) : (
          <button
            onClick={() => handleAddClick(productId)}
            disabled={isAdding}
            style={{
              background: accentColor,
              color: "white",
              border: "none",
              borderRadius: "10px",
              padding: "5px 8px",
              opacity: isAdding ? 0.6 : 1,
              cursor: "pointer",
            }}
          >
            <i className="white fa fa-plus" style={{ fontSize: "20px" }}></i>
          </button>
        )}
      </div>
    );
  };

  // Helper function to render content or empty state
  const renderContent = (data, renderFunction, showEmptyState = false) => {
    if (data && data.length > 0) {
      return renderFunction();
    }
    if (showEmptyState) {
      return null; // Don't show empty sections
    }
    return null;
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>
          AG Home Decor - Decorative Poster & Border Tiles Manufacturer
        </title>
        <meta
          name="description"
          content="Welcome to AG Home Decor. We are premium manufacturers of decorative poster tiles, border tiles, and custom designs for home decor. Everything is possible with us. Call: 8780378668"
        />
        <meta
          name="keywords"
          content="decorative poster tiles, border tiles, home decor, tile manufacturer, AG Home Decor, custom wall tiles"
        />
      </Helmet>
      <Header />

      <div className="page-content home-page" style={{ paddingBottom: "0px" }}>
        {/* ---------- BANNER SLIDER ---------- */}
        {/* 
          BANNER SIZE RECOMMENDATIONS FOR DESIGNER:
          - Desktop: 1920px × 500px (16:5 ratio)
          - Mobile: 750px × 300px (5:2 ratio)
          - Aspect Ratio: 16:5 for desktop, 5:2 for mobile
          - Format: JPG or PNG, RGB color mode
          - Resolution: 72 dpi for web
          - Max file size: 500KB per image
        */}
        <section
          className="banner-section"
          style={{
            marginTop: isMobile ? "85px" : "",
            backgroundColor: "#ffffff",
            padding: isMobile ? "0px 0" : "0px 0",
          }}
        >
          <Container className={isMobile ? "px-2" : "px-3 px-md-4"} fluid>
            <div className="banner-container">
              {renderContent(data, () => (
                <Swiper
                  centeredSlides={true}
                  autoplay={{ delay: 4000, disableOnInteraction: false }}
                  modules={[Autoplay, Pagination, Navigation]}
                  className="banner-swiper"
                  loop={true}
                  slidesPerView={1}
                  spaceBetween={isMobile ? 10 : 20}
                  pagination={{
                    clickable: true,
                    dynamicBullets: true,
                    bulletActiveClass: "swiper-pagination-bullet-active-white",
                  }}
                  style={{
                    height: isMobile ? "300px" : "500px",
                    borderRadius: "12px",
                  }}
                >
                  {data.map((item) => (
                    <SwiperSlide key={item.offetr_id}>
                      <div className="banner-card">
                        <div className="banner-image-container">
                          <img
                            className="banner-image"
                            src={
                              isMobile
                                ? item.banner_image
                                : item.banner_image_web
                            }
                            alt="Banner"
                            loading="lazy"
                            style={{
                              objectFit: "contain",
                              backgroundColor: "#ffffff",
                            }}
                          />
                        </div>
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
              ))}
            </div>
          </Container>
        </section>

        {/* ---------- QUICK ACTIONS ---------- */}
        <section
          className="quick-actions-section"
          style={{ backgroundColor: "#ffffff" }}
        >
          <Container
            className={isMobile ? "px-2" : ""}
            style={{ maxWidth: "100%" }}
          >
            <Row className={isMobile ? "g-2 mb-3" : "g-3 mb-4"}>
              <Col xs={6} md={3}>
                <Link to="/MyTransaction" className="quick-action-link">
                  <div className="quick-action-card">
                    <div className="action-content">
                      <div className="action-icon">
                        <i className="fa fa-boxes"></i>
                      </div>
                      <h5 className="action-title">My Transaction</h5>
                    </div>
                  </div>
                </Link>
              </Col>
              <Col xs={6} md={3}>
                <Link to="/MyOrderds" className="quick-action-link">
                  <div className="quick-action-card">
                    <div className="action-content">
                      <div className="action-icon">
                        <i className="fa fa-shopping-cart"></i>
                      </div>
                      <h5 className="action-title">Order Status</h5>
                    </div>
                  </div>
                </Link>
              </Col>
              <Col xs={6} md={3}>
                <Link to="/Mycart" className="quick-action-link">
                  <div className="quick-action-card">
                    <div className="action-content">
                      <div className="action-icon">
                        <i className="fa fa-clipboard-list"></i>
                      </div>
                      <h5 className="action-title">My Selection</h5>
                    </div>
                  </div>
                </Link>
              </Col>
              <Col xs={6} md={3}>
                <Link to="/AddProduct" className="quick-action-link">
                  <div className="quick-action-card">
                    <div className="action-content">
                      <div className="action-icon">
                        <i className="fa fa-list"></i>
                      </div>
                      <h5 className="action-title">Product List</h5>
                    </div>
                  </div>
                </Link>
              </Col>
            </Row>
          </Container>
        </section>

        {/* ---------- PROMOTIONAL VIDEOS (Reels Style) ---------- */}
        {renderContent(
          data2.filter((i) => i.promotion_type === "2"),
          () => (
            <section
              className="reels-section"
              style={{ backgroundColor: "#ffffff" }}
            >
              <Container fluid className={isMobile ? "px-2" : "px-3 px-md-4"}>
                <h2 className="section-title">Promotional Videos</h2>

                <div className="reels-container Promotional">
                  <Swiper
                    direction={isMobile ? "horizontal" : "horizontal"}
                    autoplay={{ delay: 4000, disableOnInteraction: false }}
                    modules={[Autoplay, Navigation]}
                    className="reels-swiper"
                    spaceBetween={isMobile ? 2 : 4}
                    slidesPerView={isMobile ? 1.8 : 5.2}
                    centeredSlides={false}
                    loop={true}
                  >
                    {data2
                      .filter((i) => i.promotion_type === "2")
                      .map((item) => (
                        <SwiperSlide key={item.promotion_id}>
                          <Link
                            to={`/PromotionalImage/${item.promotion_id}/2`}
                            className="reel-link"
                          >
                            <div className="reel-card">
                              <div className="reel-video-container">
                                <img
                                  src={
                                    item.promotion_video_thumbnail ||
                                    "https://footprintapps.in/ag_home_decor/AG.png"
                                  }
                                  alt="Promotional Video"
                                  className="reel-image"
                                  loading="lazy"
                                />
                                <div className="video-play-overlay">
                                  <div className="play-button">
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width={isMobile ? "20" : "24"}
                                      height={isMobile ? "20" : "24"}
                                      fill="white"
                                      viewBox="0 0 24 24"
                                    >
                                      <path d="M8 5v14l11-7z" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Link>
                        </SwiperSlide>
                      ))}
                  </Swiper>
                </div>
              </Container>
            </section>
          ),
          true,
        )}

        {/* ---------- GALLERY IMAGES (Reels Style) ---------- */}
        {renderContent(
          data2.filter((i) => i.promotion_type === "1"),
          () => (
            <section
              className="gallery-section"
              style={{ backgroundColor: "#ffffff" }}
            >
              <Container fluid className={isMobile ? "px-2" : "px-3 px-md-4"}>
                <h2 className="section-title">Gallery Images</h2>

                <div className="gallery-container">
                  <Swiper
                    direction={isMobile ? "horizontal" : "horizontal"}
                    autoplay={{ delay: 3500, disableOnInteraction: false }}
                    modules={[Autoplay, Navigation]}
                    className="gallery-swiper"
                    spaceBetween={isMobile ? 2 : 4}
                    slidesPerView={isMobile ? 1.8 : 5.2}
                    centeredSlides={false}
                    loop={true}
                  >
                    {data2
                      .filter((i) => i.promotion_type === "1")
                      .map((item) => (
                        <SwiperSlide key={item.promotion_id}>
                          <Link
                            to={{
                              pathname: `/PromotionalImage/${item.promotion_id}/1`,
                              state: { stateParam: true },
                            }}
                            className="gallery-link"
                          >
                            <div className="gallery-card">
                              <div className="gallery-image-container">
                                <img
                                  src={
                                    item.promotion_attechment ||
                                    "https://footprintapps.in/ag_home_decor/AG.png"
                                  }
                                  alt="Gallery Image"
                                  className="gallery-image"
                                  loading="lazy"
                                />
                              </div>
                            </div>
                          </Link>
                        </SwiperSlide>
                      ))}
                  </Swiper>
                </div>
              </Container>
            </section>
          ),
          true,
        )}

        {/* ---------- OFFER SECTION WITH ADD TO CART ---------- */}
        {renderContent(
          offer,
          () => (
            <>
              {offer.map((off) => (
                <section
                  key={off.offer_id}
                  className="offer-section"
                  style={{ backgroundColor: "#ffffff" }}
                >
                  <Container
                    fluid
                    className={isMobile ? "px-2" : "px-3 px-md-4"}
                  >
                    <h2 className="section-title">{off.offer_name}</h2>

                    <div className="offer-container">
                      <Swiper
                        direction={isMobile ? "horizontal" : "horizontal"}
                        modules={[Autoplay, Navigation]}
                        className="offer-swiper"
                        spaceBetween={isMobile ? 2 : 4}
                        slidesPerView={isMobile ? 1.6 : 5.5}
                        centeredSlides={false}
                        loop={true}
                        style={{ height: isMobile ? "300px" : "412px" }}
                      >
                        {off.products.map((item) => (
                          <SwiperSlide key={item.product_id}>
                            <div className="offer-product-card">
                              <div
                                className="offer-card"
                                style={{ height: "95%", position: "relative" }}
                              >
                                <Link
                                  to={{
                                    pathname: `/ProductDetails/${
                                      item.product_id
                                    }/${item.product_design_name
                                      .trim()
                                      .split(/\s+/)
                                      .map(
                                        (word) =>
                                          word.charAt(0).toUpperCase() +
                                          word.slice(1).toLowerCase(),
                                      )
                                      .join("")}`,
                                    state: { stateParam: true },
                                  }}
                                  className="offer-link"
                                  onClick={() =>
                                    handleProductClick(item.product_id)
                                  }
                                >
                                  {item.alert && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "0px",
                                        right: "0px",
                                        background: "#DB4437",
                                        color: "#ffffff",
                                        padding: "0px 7px",
                                        borderRadius: "0px 0px 0px 10px",
                                        fontSize: "12px",
                                        zIndex: 99,
                                      }}
                                    >
                                      {item.alert}
                                    </div>
                                  )}
                                  <div className="offer-image-container">
                                    <img
                                      src={
                                        item.product_image ||
                                        "https://footprintapps.in/ag_home_decor/AG.png"
                                      }
                                      alt={item.product_design_name}
                                      className="offer-image"
                                      loading="lazy"
                                    />
                                  </div>
                                </Link>
                                {/* ADD TO CART BUTTON */}
                                <AddToCartButton product={item} />
                              </div>
                              <center>
                                <p
                                  style={{
                                    marginBottom: "0px",
                                    color: "#666666",
                                  }}
                                >
                                  {item.product_design_name}
                                </p>
                              </center>
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </Container>
                </section>
              ))}
            </>
          ),
          true,
        )}

        {/* ---------- CUSTOMER EXPERIENCE ---------- */}
        <section
          className="testimonials-section"
          style={{
            marginTop: "30px",
            marginBottom: isMobile ? "20px" : "30px",
            backgroundColor: "#ffffff",
          }}
        >
          {renderContent(
            customerExperience,
            () => (
              <Container
                fluid
                className={isMobile ? "px-2" : "px-3 px-md-4"}
                style={{ height: isMobile ? "auto" : "195px" }}
              >
                <h2 className="section-title">Customer Experiences</h2>
                <div
                  className="swiper-container Experiences"
                  style={{
                    height: isMobile ? "auto" : "30%",
                    marginBottom: "20px",
                  }}
                >
                  <Swiper
                    autoplay={{ delay: 4000, disableOnInteraction: false }}
                    modules={[Autoplay, Navigation]}
                    className="content-swiper"
                    spaceBetween={isMobile ? 2 : 4}
                    slidesPerView={isMobile ? 1.2 : 4.5}
                    centeredSlides={false}
                    loop={true}
                    style={{ paddingTop: isMobile ? "" : "15px" }}
                  >
                    {customerExperience.map((item) => (
                      <SwiperSlide key={item.promotion_comment_id}>
                        <Link
                          to={{
                            pathname: `/PromotionalComment/${item.promotion_id}/3`,
                            state: { stateParam: true },
                          }}
                          className="content-link"
                          style={{ height: isMobile ? "auto" : "30%" }}
                        >
                          <div className="testimonial-card">
                            <div className="testimonial-content">
                              <p className="testimonial-text">
                                {item.promotion_comment.length >
                                (isMobile ? 80 : 120)
                                  ? `${item.promotion_comment.substring(
                                      0,
                                      isMobile ? 80 : 120,
                                    )}...`
                                  : item.promotion_comment}
                              </p>
                              <div className="testimonial-footer">
                                <span className="customer-name">
                                  {item.dealer_firm_name}
                                </span>
                                <span className="testimonial-date">
                                  {item.c_date}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </Container>
            ),
            true,
          )}
        </section>

        {totalDealer ? (
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "10px",
              margin: isMobile ? "20px 20px 20px" : "0px 20px 20px 20px",
              padding: "0px 16px 1px",
              marginBottom: isMobile ? "90px" : "20px",
              border: "1px solid #e0e0e0",
            }}
          >
            <p
              style={{
                fontSize: "20px",
                padding: "15px",
                marginBottom: "0px",
                color: "rgb(219, 68, 55)",
              }}
            >
              Satisfied Customer{" "}
              <span style={{ fontSize: "28px", float: "right", color: "#000" }}>
                {totalDealer ? totalDealer : "0"}
              </span>
            </p>
          </div>
        ) : (
          ""
        )}

        {/* ---------- IMAGE MODAL ---------- */}
        {image && isVisible && (
          <div
            className="image-modal-overlay"
            onClick={(e) => {
              if (e.target.classList.contains("image-modal-overlay")) {
                setIsVisible(false);
                sessionStorage.setItem("imageSeen", "true");
              }
            }}
          >
            <div className="image-modal">
              <div
                className="modal-close"
                onClick={() => {
                  setIsVisible(false);
                  sessionStorage.setItem("imageSeen", "true");
                }}
              >
                <i className="fa fa-times"></i>
              </div>
              <div className="modal-content">
                <TransformWrapper>
                  <TransformComponent>
                    <img src={image} alt="Zoomed" className="modal-image" />
                  </TransformComponent>
                </TransformWrapper>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />

      {/* CSS Styles */}
      <style jsx>{`
        /* Main theme colors */
        :root {
          --primary-bg: #ffffff;
          --secondary-bg: #f8f9fa;
          --text-primary: #000000;
          --text-secondary: #666666;
          --accent-color: rgb(219, 68, 55);
          --border-color: #e0e0e0;
          --card-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          --hover-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }

        /* Safari-specific fixes */
        .home-page {
          -webkit-overflow-scrolling: touch; /* Smooth scrolling for Safari */
          -webkit-tap-highlight-color: transparent; /* Remove tap highlight */
          background-color: var(--primary-bg) !important;
        }

        .Promotional .swiper-slide {
          margin-right: 22px !important;
          width: ${isMobile ? "180px !important" : "255px !important"};
        }
        .gallery-container .swiper-slide {
          margin-right: 22px !important;
          width: ${isMobile ? "180px !important" : "255px !important"};
        }
        .offer-container .swiper-slide {
          margin-right: 22px !important;
        }
        .Experiences .swiper-slide {
          margin-right: 22px !important;
        }

        .Experiences .swiper {
          height: ${isMobile ? "" : "170px"};
        }

        .swiper-horizontal {
          height: ${isMobile ? "170px" : "476px"};
        }

        /* Pagination bullet color for white background */
        .swiper-pagination-bullet {
          background: rgba(0, 0, 0, 0.3);
        }

        .swiper-pagination-bullet-active {
          background: var(--accent-color) !important;
        }

        /* Home Page Styles - White theme */
        .home-page {
          background: var(--primary-bg);
          min-height: 100vh;
          color: var(--text-primary);
          -webkit-font-smoothing: antialiased; /* Better text rendering */
        }

        /* Banner Section - Updated for white theme */
        .banner-section {
          padding: ${isMobile ? "0px 0" : "0px 0"};
          background-color: var(--primary-bg);
        }

        .banner-container {
          margin-top: ${isMobile ? "0px" : "0px"};
          margin-bottom: ${isMobile ? "5px" : "10px"};
        }

        .banner-card {
          border-radius: ${isMobile ? "12px" : "12px"};
          overflow: hidden;
          -webkit-transform: translateZ(0); /* Hardware acceleration */
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .banner-image-container {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: ${isMobile ? "12px" : "12px"};
          overflow: hidden;
          background-color: var(--primary-bg);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .banner-image {
          max-height: 100%;
          max-width: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          border-radius: ${isMobile ? "12px" : "12px"};
          -webkit-user-drag: none; /* Prevent image drag in Safari */
          background-color: var(--primary-bg);
        }

        /* Quick Actions - White theme */
        .quick-actions-section {
          padding: ${isMobile ? "10px 0 0px" : "0px"};
          background-color: var(--primary-bg);
        }

        .quick-action-link {
          text-decoration: none;
          -webkit-tap-highlight-color: transparent; /* Remove tap highlight */
          display: block;
          height: 100%;
        }

        .quick-action-card {
          background: var(--primary-bg);
          border-radius: ${isMobile ? "8px" : "12px"};
          padding: ${isMobile ? "15px" : "20px"};
          height: ${isMobile ? "70px" : "100px"};
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          border: 1px solid var(--border-color);
          box-shadow: var(--card-shadow);
          -webkit-transform: translateZ(0); /* Hardware acceleration */
          width: 100%;
        }

        .quick-action-card:hover {
          transform: translateY(-3px);
          box-shadow: var(--hover-shadow);
          border-color: var(--accent-color);
        }

        .action-content {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          width: 100%;
          gap: 12px;
        }

        .action-icon {
          width: ${isMobile ? "32px" : "40px"};
          height: ${isMobile ? "32px" : "40px"};
          background: rgba(219, 68, 55, 0.1);
          border-radius: ${isMobile ? "6px" : "10px"};
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .action-icon i {
          color: var(--accent-color) !important;
          font-size: ${isMobile ? "14px" : "18px"};
        }

        .action-title {
          color: var(--text-primary);
          font-size: ${isMobile ? "16px" : "18px"};
          font-weight: 600;
          margin: 0;
          line-height: 1.2;
          text-align: right;
          margin-right: 25px;
          flex: 1;
        }

        /* Reels Style Sections - White theme */
        .reels-section,
        .gallery-section,
        .offer-section {
          margin-top: ${isMobile ? "10px" : ""};
          margin: ${isMobile ? "0px 0" : "0px 0"};
          padding: 0;
          background-color: var(--primary-bg);
        }
        .gallery-section {
        }

        .reels-container,
        .gallery-container,
        .offer-container {
          width: 100%;
          overflow: hidden;
        }

        .reels-swiper,
        .gallery-swiper,
        .offer-swiper {
          padding: ${isMobile ? "5px 0" : "10px 0"};
          width: 100%;
        }

        .reel-link,
        .gallery-link,
        .offer-link {
          text-decoration: none;
          display: block;
          height: 100%;
          -webkit-tap-highlight-color: transparent;
        }

        .reel-card,
        .gallery-card,
        .offer-card {
          position: relative;
          border-radius: ${isMobile ? "8px" : "12px"};
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: var(--card-shadow);
          background: var(--primary-bg);
          -webkit-transform: translateZ(0);
          border: 1px solid var(--border-color);
        }

        .reel-card:hover,
        .gallery-card:hover,
        .offer-card:hover {
          transform: scale(1.02);
          box-shadow: var(--hover-shadow);
          border-color: var(--accent-color);
        }

        .reel-video-container,
        .gallery-image-container,
        .offer-image-container {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background-color: var(--primary-bg);
        }

        .reel-image,
        .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: transform 0.3s ease;
          -webkit-user-drag: none;
          background-color: var(--primary-bg);
        }

        .offer-image {
          width: 100%;
          height: 100%;
          object-fit: fill;
          transition: transform 0.3s ease;
          -webkit-user-drag: none;
          background-color: var(--primary-bg);
        }

        .reel-card:hover .reel-image,
        .gallery-card:hover .gallery-image,
        .offer-card:hover .offer-image {
          transform: scale(1.05);
        }

        .video-play-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 1;
          transition: opacity 0.3s ease;
        }

        .reel-card:hover .video-play-overlay {
          opacity: 0.8;
        }

        .play-button {
          width: ${isMobile ? "40px" : "50px"};
          height: ${isMobile ? "40px" : "50px"};
          background: rgba(219, 68, 55, 0.9);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transform: scale(1);
          transition: transform 0.3s ease;
        }

        .reel-card:hover .play-button {
          transform: scale(1.1);
        }

        /* Section Titles */
        .section-title {
          color: var(--text-primary);
          font-size: ${isMobile ? "18px" : "24px"};
          font-weight: 700;
          margin-bottom: ${isMobile ? "15px" : "0px"};
          padding-bottom: 8px;
          border-bottom: 2px solid var(--accent-color);
          display: inline-block;
        }

        /* Testimonials - White theme */
        .testimonial-card {
          background: var(--primary-bg);
          border-radius: ${isMobile ? "8px" : "12px"};
          padding: ${isMobile ? "15px" : "15px"};
          height: ${isMobile ? "169px" : "150px"};
          border: 1px solid var(--border-color);
          transition: all 0.3s ease;
          box-shadow: var(--card-shadow);
          -webkit-transform: translateZ(0);
        }

        .testimonial-card:hover {
          transform: translateY(-3px);
          box-shadow: var(--hover-shadow);
          border-color: var(--accent-color);
        }

        .testimonial-content {
          height: 100%;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .testimonial-text {
          color: var(--text-secondary);
          font-size: ${isMobile ? "12px" : "14px"};
          line-height: 1.4;
          margin: 0;
          flex-grow: 1;
          word-wrap: break-word; /* Safari text wrapping */
          overflow-wrap: break-word;
        }

        .testimonial-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: ${isMobile ? "10px" : "15px"};
        }

        .customer-name {
          color: var(--text-primary);
          font-size: ${isMobile ? "10px" : "12px"};
          font-weight: 600;
        }

        .testimonial-date {
          color: #888;
          font-size: ${isMobile ? "9px" : "11px"};
        }

        /* Image Modal - White theme */
        .image-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.9);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px); /* Safari backdrop */
          z-index: 1050;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: ${isMobile ? "10px" : "20px"};
          animation: fadeIn 0.3s ease-in-out;
          -webkit-animation: fadeIn 0.3s ease-in-out;
        }

        .image-modal {
          position: relative;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: ${isMobile ? "12px" : "20px"};
          padding: ${isMobile ? "10px" : "15px"};
          max-width: 90vw;
          max-height: 80vh;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }

        .modal-close {
          position: absolute;
          top: ${isMobile ? "10px" : "15px"};
          right: ${isMobile ? "10px" : "15px"};
          cursor: pointer;
          z-index: 9999;
          color: white;
          font-size: ${isMobile ? "16px" : "20px"};
          opacity: 0.8;
          transition: opacity 0.2s ease;
          -webkit-tap-highlight-color: transparent;
        }

        .modal-close:hover {
          opacity: 1;
        }

        .modal-content {
          text-align: center;
          height: 100%;
        }

        .modal-image {
          object-fit: contain;
          width: 100%;
          height: auto;
          max-height: 70vh;
          border-radius: ${isMobile ? "8px" : "12px"};
          cursor: grab;
          -webkit-user-drag: none;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Enhanced Mobile Responsiveness for Safari */
        @media (max-width: 480px) {
          .banner-image-container {
            height: 100% !important;
          }

          .banner-section {
            height: auto !important;
          }

          .banner-container > .swiper-horizontal {
            height: 400px !important;
          }

          .Experiences > .swiper-horizontal {
            height: 129px !important;
            margin-bottom: ${isMobile ? "0px" : ""};
          }

          .offer-container > .swiper-horizontal {
            height: 350px !important;
          }

          .swiper-horizontal {
            height: ${isMobile ? "330px !important" : "400px !important"};
          }

          .testimonial-card {
            height: 120px;
            padding: 12px;
          }

          .quick-action-card {
            height: 60px;
            padding: 12px;
          }

          .action-icon {
            width: 28px;
            height: 28px;
            margin-left: 0px;
          }

          .action-title {
            margin-right: 25px;
            font-size: 12px;
            margin-right: 10px;
          }

          .action-content {
            gap: 8px;
          }
        }

        @media (max-width: 360px) {
          .banner-image-container {
            height: 100% !important;
          }

          .banner-section {
            height: auto !important;
          }

          .action-title {
            font-size: 13px;
          }

          .action-icon {
            width: 24px;
            height: 24px;
            margin-left: 12px;
          }

          .action-icon i {
            font-size: 12px;
          }
        }

        /* Safari-specific media queries */
        @media not all and (min-resolution: 0.001dpcm) {
          @supports (-webkit-appearance: none) {
            /* Safari 10.1+ specific styles */
            .home-page {
              -webkit-overflow-scrolling: touch;
            }

            .banner-image,
            .reel-image,
            .gallery-image {
              -webkit-transform: translateZ(0);
            }

            .quick-action-card {
              -webkit-transform: translateZ(0);
            }
          }
        }

        /* Touch device optimizations for Safari */
        @media (hover: none) and (pointer: coarse) {
          .reel-card:hover,
          .gallery-card:hover,
          .offer-card:hover {
            transform: none;
          }

          .quick-action-card:hover {
            transform: none;
          }

          .testimonial-card:hover {
            transform: none;
          }
        }

        /* Fix for Safari button focus styles */
        .cta-button:focus {
          outline: none;
          -webkit-tap-highlight-color: transparent;
        }

        /* Safari flexbox fixes */
        .action-content {
          display: -webkit-flex;
          display: flex;
          -webkit-align-items: center;
          align-items: center;
          -webkit-justify-content: flex-start;
          justify-content: flex-start;
        }

        /* Prevent text size adjustment in Safari */
        .testimonial-text {
          -webkit-text-size-adjust: 100%;
        }

        /* Add to cart input number styling */
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }

        .redcolor {
          color: rgb(219, 68, 55) !important;
        }
        .white {
          color: rgb(255, 255, 255) !important;
        }
      `}</style>
    </React.Fragment>
  );
}
